<template>
  <BContainer
    class="p-0"
    fluid
  >
    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="8"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableInteractions.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Activity"
          label-for="filterActivity"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableInteractions.selected.activity"
            placeholder="filter activity here"
            input-id="filterActivity"
            label="activity_name"
            clearable
            append-to-body
            :options="(tableInteractions.options.activities)"
            :loading="(tableInteractions.fetching.activities)"
            :disabled="(tableInteractions.busy || tableInteractions.fetching.activities)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterActivities"
          >
            <template #option="{ activity_name }">
              <div
                class="text-break text-wrap"
                v-text="activity_name"
              />
            </template>
            <template #selected-option="{ activity_name }">
              <div
                class="w-175px w-md-75px w-lg-140px text-truncate"
                v-text="activity_name"
              />
            </template>
            <template #no-options="">
              no available activity
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableInteractions"
      table-class="min-h-300px"
      :provider="tableProvider"
      :fields="tableInteractions.fields"
      :filter="tableInteractions.filter"
      :filter-on="tableInteractions.filterOn"
      :sort-by="tableInteractions.sortBy"
      :sort-desc="tableInteractions.sortDesc"
      :sort-direction="tableInteractions.sortDirection"
      :total-rows="tableInteractions.totalRows"
      :current-page="tableInteractions.currentPage"
      :per-page="tableInteractions.perPage"
      :stacked="tableInteractions.stacked"
      :busy="tableInteractions.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="warning"
            :disabled="tableInteractions.busy"
            @click.prevent="viewInteraction(item)"
          >
            View
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalInteraction"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-uppercase text-primary"
      >
        <BTab class="p-2 min-h-400px max-h-400px">
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-file-lines" />
              <b
                class="is-tab-active ml-1"
              >
                Details
              </b>
            </div>
          </template>
          <ValidationObserver
            ref="formInteraction"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>
                <BCol
                  cols="12"
                >
                  <BFormGroup
                    label="Date"
                    label-for="fDate"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fDate"
                      v-model="formInteraction.interaction_date"
                      type="date"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                >
                  <BFormGroup
                    label="Activity"
                    label-for="fActivity"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fActivity"
                      v-model="formInteraction.activity_name"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                >
                  <BFormGroup
                    label="Description"
                    label-for="fDescription"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormTextarea
                      id="fDescription"
                      v-model="formInteraction.description"
                      rows="3"
                      max-rows="5"
                      disabled
                    />
                  </BFormGroup>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <BTab class="p-2 min-h-400px max-h-400px">
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-paperclip" />
              <b class="is-tab-active ml-1">
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :path="formInteraction.attachment_path"
            :files="formInteraction.attachments"
            :busy="state.busy"
            :show-uploader="!state.editing"
            @onUploaded="updateFormInteractionAttachments"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import Attachment from '@/views/rsm/customer-management/interactions/Attachment.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MActivities from '@/mixins/maintenances/activities'

import { SRSMCustomerInteraction } from '@/services/rsm'

export default {
  name: 'VRSMCustomerInteractions',

  components: {
    Attachment
  },

  mixins: [
    MActivities,
    MxFormatter,
    MxMisc
  ],

  props: {
    customer: {
      type: Object,
      required: true,
      default: () => ({
        id: ''
      })
    }
  },

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formInteraction: {
        customer_id: this.customer.id,
        interaction_date: '',
        activity_id: '',
        activity_name: '',
        description: '',
        attachment_path: '',
        attachments: []
      },
      tableInteractions: {
        busy: false,
        fetching: {
          activities: false
        },
        selected: {
          activity: '',
          activityType: 'Interaction'
        },
        options: {
          activities: []
        },
        filter: {
          search: '',
          customer: this.customer.id,
          activity: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 5, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'user', label: 'Name', class: 'min-w-md-150px', formatter: this.getFullName, sortable: true },
          { mobile: 2, key: 'interaction_date', class: 'min-w-150px', formatter: this.dateShortFormatter, sortable: true },
          { mobile: 3, key: 'activity.activity_name', label: 'Activity', class: 'min-w-150px', sortable: true },
          { mobile: 4, key: 'created_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  watch: {
    'selected.activity' (activity) {
      this.formInteraction.activity_id = activity?.id || ''
    },
    'tableInteractions.selected.activity' (activity) {
      this.tableInteractions.filter.activity = activity?.id || ''
    }
  },

  mounted () {
    this.getActivities(this.tableInteractions)
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableInteractions.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        customer_id: filter.customer,
        activity_id: filter.activity
      }

      return await SRSMCustomerInteraction.get(payload).then(
        ({ data }) => {
          this.tableInteractions.totalRows = data.total_rows
          return data.items
        }
      ).catch(
        () => {
          this.tableInteractions.totalRows = 0
          return []
        }
      ).finally(() => {
        this.tableInteractions.busy = false
      })
    },

    async searchFilterActivities (search, loading) {
      this.searchActivitiesProvider(this.tableInteractions, search, loading)
    },

    updateFormInteractionAttachments (attachment) {
      this.formInteraction = Object.assign(this.formInteraction, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments
      })
    },

    viewInteraction (interaction) {
      this.state.editing = true
      this.formInteraction = Object.assign(this.formInteraction, {
        customer_id: this.customer.id,
        interaction_date: interaction.interaction_date,
        activity_id: interaction.activity_id,
        activity_name: interaction.activity.activity_name,
        description: interaction.description,
        attachment_path: interaction.attachment_path,
        attachments: interaction.attachments
      })

      this.$refs.modalInteraction.show()
    },

    async validateInteraction () {
      return new Promise((resolve, reject) => {
        this.$refs.formInteraction.validate().then(
          isFormValid => {
            if (isFormValid) {
              return resolve(isFormValid)
            }
            return reject(new Error('Invalid Form'))
          }
        )
      })
    },

    async submitInteraction () {
      return new Promise(resolve => {
        this.validateInteraction().then(
          () => {
            this.swalConfirm({
              html: this.state.editing ? 'Update this Interaction?' : 'Add Interaction?',
              confirmButtonText: this.state.editing ? 'Update' : 'Add',
              cancelButtonText: 'Discard',
              preConfirm: async () => {
                this.state.busy = true

                const createOrUpdate = async () => {
                  return this.state.editing ? this.putInteraction() : this.postInteraction()
                }

                return createOrUpdate().catch(
                  ({ message }) => {
                    this.$refs.formInteraction.setErrors(message.errors)
                  }
                ).finally(() => {
                  this.state.busy = false
                })
              }
            }).then(resolve)
          }
        ).catch(() => {
          this.swalInvalid()
        })
      })
    },

    async postInteraction () {
      return await SRSMCustomerInteraction.post(this.formInteraction).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalInteraction.hide()
            this.$refs.tableInteractions.refetch()
          })
          return data
        }
      )
    },

    async putInteraction () {
      return await SRSMCustomerInteraction.put(this.formInteraction).then(
        ({ data }) => data
      )
    }
  }
}
</script>
